// src/pages/AnalyzerGuidePage.jsx
import React from "react";

export default function GuidePage() {
  return (
    <div>
      {/* 헤더 */}
      <header className="mb-5 sm:mb-6">
        <h1 className="text-xl/7 font-semibold text-zinc-950 sm:text-xl/8 lg:text-2xl/8 dark:text-white">
          전투 분석 기록 사용 가이드
        </h1>
        <p className="mt-1 text-sm text-zinc-600 sm:text-base dark:text-zinc-400">
          화면 배치를 맞춘 뒤 프로필 → 전투 분석 순서로 인식하고, 저장/평균/비교를 활용하세요.
        </p>
      </header>

      {/* 섹션 1: 기록 방법 */}
      <section className="rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">1</span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">기록 방법</h2>
          </div>
        </div>

        <ol className="divide-y divide-zinc-100 dark:divide-white/10">
          {/* 1-1. 창 모드 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="1" title="로스트아크를 창 모드(1920×1080)로 실행" />
            <p className="text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              분석 정확도를 위해 반드시 <b className="text-zinc-900 dark:text-white">1920×1080</b> 해상도 창 모드에서 실행해 주세요.
            </p>
            <p className="mt-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              <span className="btn btn-black">화면 공유 시작</span> 을 누른 뒤, 창 탭으로 이동하여
              <b className="text-zinc-900 dark:text-white"> LOST ARK </b>를 선택하고 공유 버튼을 클릭합니다.
            </p>

            {/* 이미지 자리(전투 분석) */}
            {/* <div className="mt-3">
              <ImagePlaceholder label="화면 공유 예시 캡처 자리" />
            </div> */}
          </li>

          {/* 1-2. 캐릭터 정보 인식 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="2" title="캐릭터 정보 인식" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base dark:text-zinc-300">
              <li>캐릭터 정보(기본 단축키 P)를 열고 <b className="text-zinc-900 dark:text-white">화면 우측 상단</b>에 붙입니다.</li>
              <li>화면 공유가 실행된 상태에서 <span className="btn">캐릭터 정보 인식</span> 버튼을 클릭합니다.</li>
              <li>잘못 인식되면 <span className="btn btn-danger">캐릭터 정보 인식 재실행</span> 버튼을 클릭하세요.</li>
            </ol>

            {/* 이미지 자리(프로필) */}
            {/* <div className="mt-3">
              <ImagePlaceholder label="캐릭터 정보 캡처 자리" />
            </div> */}
          </li>

          {/* 1-3. 전투 분석기 인식 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="3" title="전투 분석기 인식" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base dark:text-zinc-300">
              <li>캐릭터 정보 인식이 완료되면, 전투 분석기 (기본 단축키 Alt + .)를 열고 <b className="text-zinc-900 dark:text-white">화면 우측 상단</b>에 붙입니다.</li>
              <li>저장하고 싶은 기록을 불러온 뒤, <span className="btn">전투 분석기 인식</span> 버튼을 클릭합니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">워로드, 기공사, 리퍼</b> 직업은 전투 분석기 표가 한 화면에 모두 보이지 않을 수 있습니다.
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  <li>전투 분석기 하단까지 <b>스크롤</b>한 뒤</li>
                  <li>
                    <span className="btn">스크롤 후 전투 분석기 인식</span> 버튼을 클릭해 하단 구간까지 정확히 인식해 주세요.
                  </li>
                </ul>
              </li>
              <li>잘못 인식되면 <span className="btn btn-danger">전투 분석기 인식 재실행</span> 을 선택하세요.</li>
            </ol>

            {/* 이미지 자리(전투 분석) */}
            {/* <div className="mt-3">
              <ImagePlaceholder label="전투 분석기 캡처 자리" />
            </div> */}
          </li>

          {/* 1-4. 저장 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="4" title="저장" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base dark:text-zinc-300">
              <li>간단한 메모와 함께 <span className="btn">저장하기</span> 버튼을 누르면 <b className="text-zinc-900 dark:text-white">로컬</b>에 저장됩니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">클라우드 저장</b>을 원하면 저장할 항목을 선택 후{" "}
                <span className="btn">클라우드에 저장</span> 을 클릭하세요.
              </li>
            </ol>
            <Callout intent="rose">클라우드 저장 시 통계 및 지표 수집에 활용됩니다.</Callout>
          </li>
        </ol>
      </section>

      {/* 섹션 2: 여러 번 저장 / 다른 캐릭터 기록 */}
      <section className="mt-5 sm:mt-6 rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">2</span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">여러 번 저장 / 다른 캐릭터 기록</h2>
          </div>
        </div>

        <div className="px-3 py-4 sm:p-4 text-sm sm:text-base text-zinc-700 space-y-3 dark:text-zinc-300">
          <div>
            <StepTitle n="1" title="같은 캐릭터에서 여러 레이드 기록 저장" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2">
              <li>
                기록 저장 후 <b className="text-zinc-900 dark:text-white">다른 레이드의 전투 분석기</b>을 띄운 뒤{" "}
                <span className="btn btn-danger">전투 분석기 인식 재실행</span> 을 선택하세요.
              </li>
              <li>
                인식이 완료되면 다시 간단한 메모와 함께 <span className="btn">저장하기</span> 를 눌러 주세요.
              </li>
            </ol>
          </div>

          <div>
            <StepTitle n="2" title="다른 캐릭터의 전투 분석 기록 저장" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2">
              <li>
                <span className="btn btn-primary">처음부터 재실행</span> 을 선택한 뒤, 해당 캐릭터로 처음 단계부터 인식 과정을 진행해 주세요.
              </li>
            </ol>
          </div>

          <Callout intent="rose">
            재실행 시 이전 인식값이 새 화면으로 갱신됩니다. 저장 전에 직업/레이드가 맞는지 한 번 더 확인하세요.
          </Callout>
        </div>
      </section>

      {/* 섹션 3: 평균 계산 방법 */}
      <section className="mt-5 sm:mt-6 rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">3</span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">평균 계산 방법</h2>
          </div>
        </div>

        <div className="px-3 py-4 sm:p-4 text-sm sm:text-base text-zinc-700 space-y-2 dark:text-zinc-300">
          <p><b className="text-zinc-900 dark:text-white">같은 직업 / 같은 레이드</b> 선택 시에만 평균 계산이 가능합니다.</p>
          <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2">
            <li>평균을 낼 레이드 기록을 모두 선택합니다.</li>
            <li><span className="btn">선택 항목 평균</span> 버튼을 클릭합니다.</li>
          </ol>
          <Callout intent="rose">선택된 항목들이 조건(같은 직업·같은 레이드)에 맞지 않으면 버튼이 비활성화됩니다.</Callout>
        </div>
      </section>

      {/* 섹션 4: 비교 방법 */}
      <section className="mt-5 sm:mt-6 rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">4</span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">비교 방법</h2>
          </div>
        </div>

        <div className="px-3 py-4 sm:p-4 text-sm sm:text-base text-zinc-700 space-y-2 dark:text-zinc-300">
          <p><b className="text-zinc-900 dark:text-white">같은 직업 / 같은 레이드</b> 선택 시에만 비교가 가능합니다.</p>
          <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2">
            <li>비교할 레이드 기록 <b className="text-zinc-900 dark:text-white">2개</b>를 선택합니다.</li>
            <li>
              <span className="btn">선택 항목 비교</span> 버튼을 클릭하면,
              <span className="ml-1">첫 번째 레이드 대비 두 번째 레이드의 지표 변화량을 확인할 수 있습니다.</span>
            </li>
          </ol>
          <Callout intent="rose">첫 번째로 선택한 항목이 비교 기준으로 사용됩니다.</Callout>
        </div>
      </section>
    </div>
  );
}

/* —————— 작은 서브 컴포넌트들 —————— */

function StepTitle({ n, title }) {
  return (
    <div className="mb-2 inline-flex items-center gap-2">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-50 text-zinc-700 text-[11px] sm:text-sm font-semibold border border-zinc-950/10 dark:bg-white/10 dark:text-zinc-300 dark:border-white/10">
        {n}
      </span>
      <h3 className="text-sm sm:text-base font-semibold text-zinc-900 dark:text-white">{title}</h3>
    </div>
  );
}

// function ImagePlaceholder({ label = "이미지 자리" }) {
//   return (
//     <div
//       className="relative w-full overflow-hidden rounded-md border border-dashed border-zinc-950/10 bg-zinc-50 dark:border-white/10 dark:bg-white/5"
//       style={{ minHeight: 140 }} // 모바일에서 높이 살짝 줄임 (PC는 컨테이너가 여백 보완)
//     >
//       <div className="absolute inset-0 flex items-center justify-center">
//         <div className="text-sm text-zinc-500 dark:text-zinc-400">
//           {label}
//         </div>
//       </div>
//     </div>
//   );
// }

function Callout({ children, intent = "zinc" }) {
  const tone =
    intent === "rose"
      ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-400/20 dark:text-rose-300 dark:border-rose-300/20"
      : "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-400/20 dark:text-zinc-300 dark:border-zinc-300/20";
  return (
    <div className={`mt-2 rounded-md border px-3 py-2 sm:px-3.5 sm:py-2.5 text-[12px] sm:text-sm leading-5 sm:leading-6 ${tone}`}>
      {children}
    </div>
  );
}
